import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { MessageSquare, Check, Loader2 } from 'lucide-react';
import { activityApi, GroupedActivity, ActivityType, EntityType } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@solid/ui';
import { cn, formatRelativeTime } from '@/lib/utils';
import { ACTIVITY_DISPLAY, activityGroupLabel, activityLink } from '@/lib/activity-display';

// Filter tabs
const filterTabs: { label: string; value: ActivityType | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Mentions', value: 'MENTION' },
  { label: 'Replies', value: 'REPLY' },
  { label: 'Tasks', value: 'TASK_ASSIGNED' },
  { label: 'Forms', value: 'FORM_ASSIGNED' },
];

// Group activities by date
function groupActivitiesByDate(activities: GroupedActivity[]): Record<string, GroupedActivity[]> {
  const groups: Record<string, GroupedActivity[]> = {};
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  for (const activity of activities) {
    const date = new Date(activity.createdAt);
    let key: string;

    if (date.toDateString() === today.toDateString()) {
      key = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      key = 'Yesterday';
    } else if (date > weekAgo) {
      key = 'Earlier This Week';
    } else {
      key = 'Older';
    }

    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(activity);
  }

  return groups;
}

export function ActivityPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [activeFilter, setActiveFilter] = useState<ActivityType | 'all'>('all');

  // Fetch grouped activities
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['activity', user?.id, activeFilter, 'grouped'],
    queryFn: () => activityApi.list({
      page: 1,
      limit: 50,
      type: activeFilter === 'all' ? undefined : activeFilter,
      grouped: true,
    }),
    enabled: !!user?.id,
  });

  // Mark all as read mutation
  const markAllReadMutation = useMutation({
    mutationFn: () => activityApi.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      queryClient.invalidateQueries({ queryKey: ['activity-unread-count'] });
    },
  });

  // Mark single as read mutation (for single-count groups)
  const markReadMutation = useMutation({
    mutationFn: (id: string) => activityApi.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      queryClient.invalidateQueries({ queryKey: ['activity-unread-count'] });
    },
  });

  // Mark group as read mutation
  const markGroupReadMutation = useMutation({
    mutationFn: (params: { entityType?: EntityType | null; entityId?: string | null; activityType: ActivityType }) =>
      activityApi.markGroupAsRead(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      queryClient.invalidateQueries({ queryKey: ['activity-unread-count'] });
    },
  });

  const activities = data?.items || [];
  const unreadCount = data?.unreadCount || 0;
  const groupedActivities = groupActivitiesByDate(activities);
  const dateGroups = ['Today', 'Yesterday', 'Earlier This Week', 'Older'];

  return (
    <div className="w-full">
      <PageHeader
        title="Activity for You"
        subtitle={
          unreadCount > 0
            ? `${unreadCount} unread ${unreadCount === 1 ? 'item' : 'items'}`
            : undefined
        }
        actions={
          unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
            >
              <Check className="w-4 h-4" />
              Mark All Read
            </Button>
          )
        }
      />

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {filterTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveFilter(tab.value)}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap',
              activeFilter === tab.value
                ? 'bg-secondary text-white'
                : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
          </div>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && activities.length === 0 && (
        <Card>
          <EmptyState
            icon={MessageSquare}
            title="No activity yet"
            description="When someone mentions you, replies to your posts, or assigns you tasks, you'll see it here."
          />
        </Card>
      )}

      {/* Activity list */}
      {!isLoading && activities.length > 0 && (
        <div className="space-y-6">
          {dateGroups.map((dateKey) => {
            const groupActivities = groupedActivities[dateKey];
            if (!groupActivities || groupActivities.length === 0) return null;

            return (
              <div key={dateKey}>
                <h3 className="text-xs font-semibold text-grey-500 uppercase tracking-wider mb-3">
                  {dateKey}
                </h3>
                <Card className="divide-y divide-grey-100 !p-0 overflow-hidden">
                  {groupActivities.map((activity) => {
                    const config = ACTIVITY_DISPLAY[activity.type];
                    const Icon = config.icon;
                    const link = activityLink(activity);
                    const hasMultiple = activity.count > 1;
                    const isUnread = activity.hasUnread || !activity.isRead;

                    return (
                      <Link
                        key={`${activity.entityType}-${activity.entityId}-${activity.type}`}
                        to={link}
                        onClick={() => {
                          if (isUnread) {
                            if (hasMultiple) {
                              // Mark entire group as read
                              markGroupReadMutation.mutate({
                                entityType: activity.entityType as EntityType | undefined,
                                entityId: activity.entityId,
                                activityType: activity.type,
                              });
                            } else {
                              markReadMutation.mutate(activity.id);
                            }
                          }
                        }}
                        className="flex items-start gap-4 p-4 hover:bg-grey-50 transition-colors"
                      >
                        {/* Unread indicator */}
                        <div className="w-2 shrink-0 pt-3">
                          {isUnread && (
                            <div className="w-2 h-2 rounded-full bg-primary" />
                          )}
                        </div>

                        {/* Icon with count badge */}
                        <div className="relative shrink-0">
                          <div className={cn(
                            'w-10 h-10 rounded-full flex items-center justify-center',
                            config.bgColor
                          )}>
                            <Icon className={cn('w-5 h-5', config.color)} />
                          </div>
                          {hasMultiple && (
                            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-primary text-white text-xs font-medium rounded-full flex items-center justify-center">
                              {activity.count}
                            </span>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-secondary">
                            {hasMultiple ? activityGroupLabel(activity.type, activity.count) : activity.title}
                          </p>
                          <p className="text-sm text-grey-500 truncate mt-0.5">
                            {hasMultiple ? `in "${activity.description || activity.title}"` : activity.description}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {activity.project && (
                              <span className="text-xs text-grey-400">
                                {activity.project.name}
                              </span>
                            )}
                            {activity.project && (
                              <span className="text-xs text-grey-300">·</span>
                            )}
                            <span className="text-xs text-grey-400">
                              {formatRelativeTime(new Date(activity.createdAt))}
                            </span>
                          </div>
                        </div>

                        {/* Actor avatars - show multiple for grouped activities */}
                        <div className="flex -space-x-2 shrink-0">
                          {hasMultiple && activity.actors ? (
                            <>
                              {activity.actors.slice(0, 3).map((actor) => (
                                <Avatar
                                  key={actor.id}
                                  src={actor.avatarUrl}
                                  name={actor.name}
                                  size="sm"
                                  className="ring-2 ring-white"
                                />
                              ))}
                              {activity.actors.length > 3 && (
                                <div className="w-8 h-8 rounded-full bg-grey-200 flex items-center justify-center text-xs font-medium text-grey-600 ring-2 ring-white">
                                  +{activity.actors.length - 3}
                                </div>
                              )}
                            </>
                          ) : activity.actor ? (
                            <Avatar
                              src={activity.actor.avatarUrl}
                              name={activity.actor.name}
                              size="sm"
                            />
                          ) : null}
                        </div>
                      </Link>
                    );
                  })}
                </Card>
              </div>
            );
          })}

          {/* Load more indicator */}
          {isFetching && !isLoading && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />
            </div>
          )}

          {/* Has more indicator */}
          {data?.pagination.hasMore && (
            <div className="text-center py-4">
              <p className="text-sm text-grey-500">
                Showing {activities.length} of {data.pagination.total} items
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

